import React, { useRef, useCallback, useContext } from 'react';
import { noop, noopString } from 'utils/noop';
import { PlayerInstance } from 'hooks/usePlayer/types';

export type PartialPlayerInstanceType = Pick<
    PlayerInstance,
    'playing' | 'setPlaying'
>;

interface SamplePlayerProviderProps {
    children: React.ReactNode;
}

type SetPlaying = (value: boolean) => void;

export type SamplePlayerContextType = {
    stopActiveSamplePlayer: () => void;
    setActivePlayerCallback: (setPlaying: SetPlaying) => void;
    setActivePlayerId: (id?: string) => void;
    getActivePlayerId: () => string | undefined;
};

export const DefaultSamplePlayerContext = {
    stopActiveSamplePlayer: noop,
    setActivePlayerCallback: noop,
    setActivePlayerId: noop,
    getActivePlayerId: noopString,
};

export const SamplePlayerContext = React.createContext<SamplePlayerContextType>(
    DefaultSamplePlayerContext,
);

export const SamplePlayerProvider = ({
    children,
}: SamplePlayerProviderProps): JSX.Element => {
    const setPlayingRef = useRef<SetPlaying | null>(null);
    const setActivePlayerIdRef = useRef<string | undefined>(undefined);

    const setActivePlayerCallback = (setPlaying: SetPlaying): void => {
        setPlayingRef.current = setPlaying;
    };

    const setActivePlayerId = (id?: string): void => {
        setActivePlayerIdRef.current = id;
    };

    const getActivePlayerId = () => {
        return setActivePlayerIdRef.current;
    };

    const stopActiveSamplePlayer = useCallback((): void => {
        setPlayingRef?.current && setPlayingRef.current(false);
        setActivePlayerIdRef.current = undefined;
    }, []);

    return (
        <SamplePlayerContext.Provider
            value={{
                stopActiveSamplePlayer,
                setActivePlayerCallback,
                setActivePlayerId,
                getActivePlayerId,
            }}
        >
            {children}
        </SamplePlayerContext.Provider>
    );
};

export const useSamplePlayerContext = (): SamplePlayerContextType =>
    useContext(SamplePlayerContext);

export default SamplePlayerProvider;
